import { type Media } from "@common/declarations";
import apiV1, { getApiPrefix } from "@common/lib/api";
import { type I18n } from "@common/lib/i18n";
import { Common } from "@common/types";

const createApi = (i18n: I18n, isHost: boolean) => {
  const apiPrefix = getApiPrefix(isHost);

  return {
    getAvailabilityProfile: (
      args: Common.System.Api.GetAvailabilityProfileRequest
    ): Promise<Common.System.AvailabilityProfile> => apiV1.get(i18n, `${apiPrefix}/system/availabilityProfiles`, args),
    getAvailabilityProfileAccessCards: (): Promise<Common.System.AvailabilityProfileAccessCard[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/availabilityProfiles/accessCards`),
    getAvailabilityProfileEntities: (
      args: Common.System.Api.GetAvailabilityProfileEntitiesRequest
    ): Promise<Common.System.AvailabilityProfileEntity[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/availabilityProfiles/entities`, args),
    updateAvailabilityProfiles: (args: Common.System.Api.UpdateAvailabilityProfilesRequest): Promise<void> =>
      apiV1.put(i18n, `${apiPrefix}/system/availabilityProfiles`, args),

    getHistory: (args: Common.System.Api.GetHistoryRequest): Promise<Common.System.Api.HistoryItem[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/history`, args),
    createHistory: (data: Common.System.Api.CreateHistoryRequest): Promise<Common.System.Api.HistoryItem> =>
      apiV1.post(i18n, `${apiPrefix}/system/history`, data),
    getExecution: (key: Common.System.Api.ExecutionKey): Promise<Common.System.Api.Execution> =>
      apiV1.get(i18n, `${apiPrefix}/system/executions`, { key }),

    addField: (data: Common.System.Api.CreateFormFieldRequest): Promise<Common.System.Api.FormField> =>
      apiV1.post(i18n, `${apiPrefix}/system/formFields`, data),
    deleteField: (id: string): Promise<void> => apiV1.delete(i18n, `${apiPrefix}/system/formFields/${id}`),
    getFields: (args: Common.System.Api.GetFormFieldsRequest): Promise<Common.System.Api.FormField[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/formFields`, args),
    updateField: (data: Common.System.Api.UpdateFormFieldRequest): Promise<Common.System.Api.FormField> =>
      apiV1.put(i18n, `${apiPrefix}/system/formFields`, data),

    addPageFormField: (
      formId: string,
      data: Common.System.Api.CreatePageFormFieldRequest
    ): Promise<Common.System.Api.PageFormField> => apiV1.post(i18n, `${apiPrefix}/system/forms/${formId}/fields`, data),
    getPageFormFields: (
      formId: string,
      args: Common.System.Api.GetPageFormFieldsRequest
    ): Promise<Common.System.Api.PageFormField[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${formId}/fields`, args),
    linkPageFormField: (
      formId: string,
      formFieldId: string,
      data: Common.System.Api.LinkPageFormFieldRequest
    ): Promise<Common.System.Api.PageFormField> =>
      apiV1.put(i18n, `${apiPrefix}/system/forms/${formId}/fields/${formFieldId}/link`, data),
    movePageFormField: (
      formId: string,
      formPageId: string,
      formFieldId: string,
      data: Common.System.Api.MovePageFormFieldRequest
    ): Promise<Common.System.Api.PageFormField> =>
      apiV1.put(i18n, `${apiPrefix}/system/forms/${formId}/pages/${formPageId}/fields/${formFieldId}/move`, data),
    unlinkPageFormField: (formId: string, pageId: string, id: string): Promise<void> =>
      apiV1.put(i18n, `${apiPrefix}/system/forms/${formId}/pages/${pageId}/fields/${id}/unlink`),
    updatePageFormField: (
      formId: string,
      data: Common.System.Api.UpdatePageFormFieldRequest
    ): Promise<Common.System.Api.PageFormField> => apiV1.put(i18n, `${apiPrefix}/system/forms/${formId}/fields`, data),

    getForms: (type: Common.System.Api.FormTypeKey): Promise<Common.System.Api.Form[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms`, { formTypeKey: type }),
    getForm: (id: string): Promise<Common.System.Api.Form> => apiV1.get(i18n, `${apiPrefix}/system/forms/${id}`),
    addForm: (data: Common.System.Api.AddFormRequest): Promise<Common.System.Api.Form> =>
      apiV1.post(i18n, `${apiPrefix}/system/forms`, data),
    updateForm: (data: Common.System.Api.UpdateFormRequest): Promise<Common.System.Api.Form> =>
      apiV1.put(i18n, `${apiPrefix}/system/forms`, data),
    deleteForm: async (id: string): Promise<void> => apiV1.delete(i18n, `${apiPrefix}/system/forms/${id}`),

    deleteFormUser: (id: string, userId: number): Promise<void> =>
      apiV1.delete(i18n, `${apiPrefix}/system/forms/${id}/users/${userId}`),
    getFormUser: (id: string, userId: number): Promise<Common.System.Api.FormUser> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${id}/users/${userId}`),
    getFormUsers: (id: string, data: Common.Api.PageRequest): Promise<Common.System.Api.FormUser[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${id}/users`, data),
    exportFormUsers: (id: string) => apiV1.download(`${apiPrefix}/system/forms/${id}/users/export`),
    exportFormUser: (id: string, userId: number) =>
      apiV1.download(`${apiPrefix}/system/forms/${id}/users/${userId}/export`),

    getFormUserResponses: (id: string, userId: number): Promise<Common.System.Api.FormUserResponse[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${id}/users/${userId}/responses`),

    addFormPage: (id: string, data: Common.System.Api.AddFormPageRequest): Promise<Common.System.Api.FormPage> =>
      apiV1.post(i18n, `${apiPrefix}/system/forms/${id}/pages`, data),
    deleteFormPage: async (fid: string, id: string, data: { mustDeleteFormFields: boolean }): Promise<void> =>
      apiV1.delete(i18n, `${apiPrefix}/system/forms/${fid}/pages/${id}`, data),
    getFormPage: (fid: string, id: string): Promise<Common.System.Api.FormPage> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${fid}/pages/${id}`),
    getFormPages: (id: string): Promise<Common.System.Api.FormPage[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${id}/pages`),
    updateFormPage: (id: string, data: Common.System.Api.UpdateFormPageRequest): Promise<Common.System.Api.FormPage> =>
      apiV1.put(i18n, `${apiPrefix}/system/forms/${id}/pages`, data),

    updateFormConditions: (formId: string, data: Common.System.Api.UpdateFormConditionsRequest) =>
      apiV1.put(i18n, `${apiPrefix}/system/forms/${formId}/conditionGroups/merge`, data),

    getFormPageActions: (formId: string, formPageId: string): Promise<Common.System.Api.FormPageAction[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${formId}/pages/${formPageId}/actions`),

    getFormPageConditions: (formId: string, formPageId: string): Promise<Common.System.Api.FormPageCondition[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${formId}/pages/${formPageId}/conditions`),
    updateFormPageConditions: (
      formId: string,
      formPageId: string,
      data: Common.System.Api.UpdateFormPageConditionsRequest
    ): Promise<void> => apiV1.put(i18n, `${apiPrefix}/system/forms/${formId}/pages/${formPageId}/conditions`, data),

    getFormPageConditionGroups: (
      formId: string,
      formPageId: string
    ): Promise<Common.System.Api.FormPageConditionGroup[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${formId}/pages/${formPageId}/conditionGroups`),
    updateFormPageConditionGroups: (
      formId: string,
      formPageId: string,
      data: Common.System.Api.UpdateFormPageConditionGroupsRequest
    ) => apiV1.put(i18n, `${apiPrefix}/system/forms/${formId}/pages/${formPageId}/conditionGroups/merge`, data),

    // handy APIs for single page forms
    getFormFields: (
      formId: string,
      data?: Common.System.Api.GetPageFormFieldsRequest
    ): Promise<Common.System.Api.PageFormField[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/forms/${formId}/fields`, data),

    addBank: (data: Common.System.Api.CreateBankRequest): Promise<Common.System.Api.Bank> =>
      apiV1.post(i18n, `${apiPrefix}/system/banks`, data),
    deleteBank: (id: string): Promise<void> => apiV1.delete(i18n, `${apiPrefix}/system/banks/${id}`),
    getBanks: (data: Common.System.Api.GetBanksRequest): Promise<Common.System.Api.Bank[]> =>
      apiV1.get(i18n, `${apiPrefix}/system/banks`, data),
    updateBank: (data: Common.System.Api.UpdateBankRequest): Promise<Common.System.Api.Bank> =>
      apiV1.put(i18n, `${apiPrefix}/system/banks`, data),

    getMedia: (id: string): Promise<Media.Item> => apiV1.get(i18n, `/system/media/${id}`),
    addMedia: (data: Media.Api.CreateRequest): Promise<Media.Item> => apiV1.post(i18n, `/system/media`, data)
  };
};

export const getApi = (i18n: I18n, isHost?: boolean) => createApi(i18n, isHost ?? false);
